/**
 * Awaiting organization approval (PRD §7.2).
 *
 * Reached when a verified account has no organization — usually an unknown
 * business domain under the `pending` policy. The account exists and the
 * session is valid; only module entry is on hold.
 */
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/auth-layout';
import { DarkAlert, DarkSubmit } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';

export function AwaitingApprovalPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [stillWaiting, setStillWaiting] = useState(false);

  // Assignment happens out of band, so offer a manual re-check rather than
  // polling the server on a timer.
  const recheck = useCallback(() => {
    setChecking(true);
    setStillWaiting(false);
    void (async () => {
      try {
        const me = await refresh();
        if (me?.activeOrganization) {
          navigate('/account', { replace: true });
          return;
        }
        setStillWaiting(true);
      } catch {
        setStillWaiting(true);
      } finally {
        setChecking(false);
      }
    })();
  }, [refresh, navigate]);

  return (
    <AuthLayout
      title="Awaiting organization approval"
      description="Your account is set up, but it isn't linked to an organization yet."
      footer={
        user ? (
          <Link to="/sign-out" className="font-medium text-brand-400 hover:text-brand-300">
            Sign out
          </Link>
        ) : (
          <Link to="/sign-in" className="font-medium text-brand-400 hover:text-brand-300">
            Back to sign in
          </Link>
        )
      }
    >
      <div className="space-y-5">
        <DarkAlert tone="info">
          We couldn&apos;t match{user ? ` ${user.email}` : ' your email address'} to an existing
          MindPeer organization, so a platform administrator needs to assign you before you can
          open a module.
        </DarkAlert>

        <div className="space-y-2 text-sm text-slate-400">
          <p className="font-medium text-white">What happens next</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>A MindPeer administrator reviews your account.</li>
            <li>They assign it to the right organization.</li>
            <li>You can then sign in to any module your organization uses.</li>
          </ul>
        </div>

        {stillWaiting && (
          <DarkAlert tone="warning">
            No organization has been assigned yet. Please check back shortly.
          </DarkAlert>
        )}

        <DarkSubmit type="button" loading={checking} onClick={recheck}>
          Check again
        </DarkSubmit>
      </div>
    </AuthLayout>
  );
}
